import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess, Close as CloseIcon } from "@mui/icons-material";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import TimelineFilterBar from "./TimelineFilterBar";
import { useAuthContext } from "../../context/AuthContext";
import { getStatusChip } from "../../utils/utils";
import { getStatusLabelFromCode } from "../../utils/status";
import { useTranslation } from "react-i18next";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";
import {
  matchesTimelineFilters,
  isMasterTechnicalLogMessage,
  isVoiceCallTranscriptMessage,
  isVideoRecordingReadyLogMessage,
  isRedundantVideoCallEndedLog,
  isRedundantXproVideoRecordingAttachment,
  getDefaultTimelineFilters,
  sanitizeTimelineFilters,
  TIMELINE_FILTERS,
} from "./messageTypeRegistry";
import { getConversationAccessLogs } from "../../api/client";
import { useCanViewApiAudit } from "../../hooks/useCanViewApiAudit";
import {
  readWorkspaceLocalJson,
  writeWorkspaceLocalJson,
  WS_KEY,
} from "../../utils/workspaceStorage";

/**
 * Componente que exibe o histórico de mensagens de uma conversa.
 */
const MessageHistory = ({
  messages,
  selectedConversation,
  isLoadingConversation,
  onSendMessage,
  onEndConversation,
  onReopenRequest,
  onOpenNewRequest,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onDeleteMessage,
  onReactMessage,
  onEditWhatsappMessage,
  onForwardWhatsappMessage,
  onEditNote,
  onDeleteNote,
  onRefetchConversationDetails,
  onComposeChannelChange,
}) => {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("chat");
  const canViewApiAudit = useCanViewApiAudit();
  const [isClosedBannerCollapsed, setIsClosedBannerCollapsed] = useState(false);
  const [quoteDraft, setQuoteDraft] = useState(null);

  useEffect(() => {
    setQuoteDraft(null);
  }, [selectedConversation?.id]);
  const [timelineFilters, setTimelineFiltersState] = useState(() => {
    const stored = readWorkspaceLocalJson(WS_KEY.CHAT_TIMELINE_FILTERS, null);
    if (Array.isArray(stored)) {
      return sanitizeTimelineFilters(stored, { canViewApiAudit: true });
    }
    return null;
  });

  useEffect(() => {
    setTimelineFiltersState((prev) => {
      if (prev == null) {
        return getDefaultTimelineFilters({ canViewApiAudit });
      }
      return sanitizeTimelineFilters(prev, { canViewApiAudit });
    });
  }, [canViewApiAudit]);

  const setTimelineFilters = useCallback(
    (next) => {
      setTimelineFiltersState((prev) => {
        const raw = typeof next === "function" ? next(prev || []) : next;
        const sanitized = sanitizeTimelineFilters(raw, { canViewApiAudit });
        writeWorkspaceLocalJson(WS_KEY.CHAT_TIMELINE_FILTERS, sanitized);
        return sanitized;
      });
    },
    [canViewApiAudit]
  );

  const activeTimelineFilters = timelineFilters || [];
  const showAccessLogs = activeTimelineFilters.includes(TIMELINE_FILTERS.ACCESS);
  const [accessMessages, setAccessMessages] = useState([]);

  const isPersonalWhatsappThread =
    selectedConversation?.kind === "personal" ||
    String(selectedConversation?.id || "").startsWith("wa-personal:");

  useEffect(() => {
    if (!showAccessLogs || !selectedConversation?.id || isPersonalWhatsappThread) {
      setAccessMessages([]);
      return undefined;
    }
    let cancelled = false;
    getConversationAccessLogs(selectedConversation.id)
      .then((list) => {
        if (!cancelled) setAccessMessages(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAccessMessages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showAccessLogs, selectedConversation?.id, isPersonalWhatsappThread]);

  const filteredMessages = useMemo(() => {
    const list = [
      ...(Array.isArray(messages) ? messages : []),
      ...(showAccessLogs ? accessMessages : []),
    ];
    const withoutDupMedia = list.filter(
      (m) =>
        !isVoiceCallTranscriptMessage(m) &&
        !isVideoRecordingReadyLogMessage(m) &&
        !isRedundantVideoCallEndedLog(m, list) &&
        !isRedundantXproVideoRecordingAttachment(m, list)
    );
    const visible = canViewApiAudit
      ? withoutDupMedia
      : withoutDupMedia.filter((m) => !isMasterTechnicalLogMessage(m));
    return visible.filter((m) => matchesTimelineFilters(m, activeTimelineFilters));
  }, [messages, accessMessages, showAccessLogs, activeTimelineFilters, canViewApiAudit]);

  const rawStatus = selectedConversation?.status;
  const numericStatus =
    typeof rawStatus === "string" ? Number(rawStatus) : rawStatus;
  const isClosedStatus =
    numericStatus === 2 || numericStatus === 4 || numericStatus === 5;

  if (isLoadingConversation) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 2,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="20%" sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
          <Skeleton variant="rounded" width="70%" height={48} />
          <Skeleton variant="rounded" width="55%" height={48} sx={{ alignSelf: "flex-end" }} />
          <Skeleton variant="rounded" width="65%" height={48} />
          <Skeleton variant="rounded" width="50%" height={48} sx={{ alignSelf: "flex-end" }} />
        </Box>
        <Box sx={{ pt: 2 }}>
          <Skeleton variant="rounded" width="100%" height={COMPOSER_ROW_HEIGHT_PX} />
        </Box>
      </Box>
    );
  }

  if (!selectedConversation) {
    return (
      <Typography variant="h6" align="center" p={2}>
        {t("history.selectConversation")}
      </Typography>
    );
  }

  const personalThreadClientLabel = isPersonalWhatsappThread
    ? String(
        (selectedConversation?.name && String(selectedConversation.name).trim()) ||
          (selectedConversation?.nome && String(selectedConversation.nome).trim()) ||
          (selectedConversation?.celular && String(selectedConversation.celular).trim()) ||
          ""
      ).trim() || null
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Lista de mensagens com virtualização */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <TimelineFilterBar
          value={activeTimelineFilters}
          onChange={setTimelineFilters}
          showApiLogsFilter={canViewApiAudit}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: 2,
            pt: 0.5,
            pb: 0.5,
            "@media (max-width: 768px)": { px: 1.25 },
          }}
        >
          <MessageList
          key={selectedConversation.id}
          messages={filteredMessages}
          loggedUser={userData}
          onSendMessage={onSendMessage}
          defaultChannel={
            selectedConversation?.channel ||
            selectedConversation?.channelName ||
            selectedConversation?.channelType ||
            ""
          }
          conversationChannelId={selectedConversation?.channelId ?? null}
          conversationChannelLabel={
            selectedConversation?.channel ||
            selectedConversation?.channelName ||
            null
          }
          personalThreadClientLabel={personalThreadClientLabel}
          hasMore={hasMoreMessages}
          isLoadingMore={isLoadingMoreMessages}
          onLoadMore={onLoadMoreMessages}
          onEndConversation={onEndConversation}
          onDeleteMessage={onDeleteMessage}
          onReactMessage={onReactMessage}
          onEditWhatsappMessage={onEditWhatsappMessage}
          onForwardWhatsappMessage={onForwardWhatsappMessage}
          onQuoteWhatsappMessage={(message) => {
            const stored = String(message?.id_msg_whatsapp || message?.idMsgWhatsapp || "").trim();
            if (!stored && !message?.json_whatsapp) return;
            setQuoteDraft({
              ...message,
              id_msg_whatsapp: stored || message?.id_msg_whatsapp,
            });
          }}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          conversationProjectId={selectedConversation?.projectId ?? null}
          selectedConversation={selectedConversation}
        />
        </Box>
      </Box>

      {/* Campo de entrada de mensagens / aviso de atendimento encerrado */}
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: theme.palette.background.paper,
          padding: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          "@media (max-width: 768px)": {
            px: 1,
            pt: 0.5,
            pb: 0.5,
          },
        }}
      >
        {isClosedStatus ? (
          isClosedBannerCollapsed ? (
            <Alert
              severity="info"
              icon={false}
              sx={{
                borderRadius: 2,
                py: 0.25,
                px: 0.75,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor:
                  theme.palette.mode === "light"
                    ? theme.palette.grey[50]
                    : theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <IconButton
                size="small"
                aria-label={t("history.showClosedDetailsAria")}
                onClick={() => setIsClosedBannerCollapsed(false)}
              >
                <ExpandLess fontSize="small" />
              </IconButton>
            </Alert>
          ) : (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                pt: 0.75,
                pb: 0.75,
                px: 1.75,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? theme.palette.grey[50]
                    : theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                mb: 0.75,
                position: "relative",
              }}
            >
              {/* X no canto superior direito */}
              <IconButton
                size="small"
                aria-label={t("history.closeClosedMessageAria")}
                onClick={() => setIsClosedBannerCollapsed(true)}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", md: "flex-start" }}
              >
                {/* Coluna esquerda: título + textos */}
                <Stack
                  spacing={0.5}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    pr: { md: 2 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: "wrap" }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("history.conversationClosed")}
                    </Typography>
                    {getStatusChip(numericStatus, theme)}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.25 }}
                  >
                    {t("history.closedNoSend")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.25 }}
                  >
                    {t("history.closedContinueHint")}
                  </Typography>
                </Stack>

                {/* Coluna direita: botões empilhados */}
                <Stack
                  direction="column"
                  spacing={0.75}
                  justifyContent="flex-start"
                  alignItems="stretch"
                  sx={{
                    mt: { xs: 1, md: 1 },
                    minWidth: { md: 210 },
                  }}
                >
                  {typeof onOpenNewRequest === "function" && (
                    <Chip
                      label={t("history.newRequestChip")}
                      variant="outlined"
                      clickable
                      onClick={onOpenNewRequest}
                      sx={{ fontSize: "0.78rem", height: 24 }}
                    />
                  )}
                  {typeof onReopenRequest === "function" && (
                    <Chip
                      label={t("history.reopenChip")}
                      color="primary"
                      clickable
                      onClick={onReopenRequest}
                      sx={{ fontSize: "0.78rem", height: 24 }}
                    />
                  )}
                </Stack>
              </Stack>
            </Alert>
          )
        ) : (
          <MessageInput
            onSendMessage={onSendMessage}
            selectedConversation={selectedConversation}
            messages={messages}
            hasHistory={Array.isArray(messages) && messages.length > 0}
            onRefetchConversationDetails={onRefetchConversationDetails}
            onComposeChannelChange={onComposeChannelChange}
            quoteDraft={quoteDraft}
            onClearQuote={() => setQuoteDraft(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default MessageHistory;
